import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';

import {
  Page,
  Hero,
  HeroImg,
  HeroOverlay,
  HeroContent,
  LogoBadge,
  Logo,
  HeroBadge,
  HeroTitle,
  HeroSub,
  HeroButtons,
  PrimaryButton,
  GhostButton,
  Wrap,
  Why,
  WhyCard,
  Eyebrow,
  WhyCardTitle,
  WhyCardBody,
  Partner,
  PartnerBar,
  PartnerText,
  Shop,
  ShopHeader,
  ShopHeading,
  CatGrid,
  CatTile,
  CatTileSwatch,
  CatTileImg,
  CatTileBody,
  CatTileCta,
  CatTileCtaBody,
  Trust,
  TrustItem,
  TrustNum,
  TrustNumSmall,
  TrustLabel,
  Testimonial,
  TestimonialMark,
  TestimonialBody,
  TestimonialAttr,
  Faq,
  FaqHeading,
  FaqItem,
  FaqQuestion,
  FaqIcon,
  FaqAnswer,
  FooterCta,
  FooterCtaText,
  FooterCtaSub,
  SubText,
} from './PandGPage.style';
import pandgLogo from '../../images/pandg.png';
import LinkifiedText from '../../components/LinkifiedText/LinkifiedText';
import {setCurrentRoute} from '../../actions';
import {MESSAGES, ROUTES} from '../../constants/constants';
import {
  PANDG_ALL_CATEGORY,
  PANDG_BANNER_IMAGE_URL,
  PANDG_BROCHURE_URL,
  PANDG_CATEGORIES,
  PANDG_FAQ_ITEMS,
  PANDG_SWATCHES,
} from '../../constants/search.constants';
import {setPageHeadTags} from '../../helpers/seo.helper';
import {getPandgSearchUrl} from '../../helpers/search.helper';

const PandGPage = () => {
  const dispatch = useDispatch();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  useEffect(() => {
    dispatch(setCurrentRoute(ROUTES.PANDG));
    setPageHeadTags({
      title: 'P&G PRO at JJ Foodservice',
      description:
        'JJ Foodservice is the official UK Foodservice Distributor of P&G PRO — Fairy, Flash, Ariel and Lenor Professional, on your JJ account, on your next delivery.',
    });
  }, [dispatch]);

  const toggleFaq = index =>
    setOpenFaqIndex(current => (current === index ? null : index));

  // SearchPage renders as a sibling swap under the same scroll container, so
  // navigating there while scrolled down on this long page leaves the scroll
  // position where it was — reset it explicitly on every outbound link here.
  // The re-assert after two frames guards against anything (results reflow,
  // mobile browser focus handling) nudging the scroll back down again after
  // the initial reset, the same class of issue seen on SearchPage's pills.
  const scrollToTop = () => {
    window.scrollTo(0, 0);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => window.scrollTo(0, 0))
    );
  };

  return (
    <Page>
      <Hero>
        <HeroImg
          src={PANDG_BANNER_IMAGE_URL}
          alt="JJ Foodservice delivering P&G PRO products to a London hotel"
        />
        <HeroOverlay />
        <HeroContent>
          <LogoBadge>
            <Logo
              src={pandgLogo}
              alt="JJ Foodservice in partnership with P&G PRO"
            />
          </LogoBadge>
          {/* <HeroBadge>OFFICIAL UK DISTRIBUTOR &mdash; P&amp;G PRO</HeroBadge> */}
          <HeroTitle>Trusted P&G PRO Cleaning Solutions</HeroTitle>
          <HeroSub>
            Now available through JJ Foodservice
            <br />
          </HeroSub>
          <HeroButtons>
            <PrimaryButton
              to={getPandgSearchUrl(PANDG_ALL_CATEGORY)}
              onClick={scrollToTop}
            >
              Shop the range
            </PrimaryButton>
            <GhostButton
              href={PANDG_BROCHURE_URL}
              target="_blank"
              rel="noopener"
            >
              Download brochure
            </GhostButton>
          </HeroButtons>
        </HeroContent>
      </Hero>

      <Wrap>
        <Why>
          <WhyCard>
            <Eyebrow>Why P&amp;G PRO?</Eyebrow>
            <WhyCardTitle>
              Industry-leading cleaning, built for foodservice
            </WhyCardTitle>
            <WhyCardBody>
              Commercial-grade formulas – faster grease-cutting, fewer
              re-washes, less waste. Used across hotels, restaurants and
              professional kitchens worldwide.
            </WhyCardBody>
          </WhyCard>
          <WhyCard>
            <Eyebrow>Why buy it at JJ</Eyebrow>
            <WhyCardTitle>One account, one delivery</WhyCardTitle>
            <WhyCardBody>
              No separate supplier, no second invoice – cleaning arrives with
              your food order.
            </WhyCardBody>
          </WhyCard>
        </Why>
      </Wrap>

      <Wrap>
        <Partner>
          <PartnerBar />
          <PartnerText>
            JJ Foodservice has supported UK hospitality since 1988. Now,
            stocking P&G PRO products, we bring that same trusted range directly
            alongside your food and drink orders.
          </PartnerText>
        </Partner>
      </Wrap>

      <Wrap>
        <Shop>
          <ShopHeader>
            <ShopHeading>Shop by Category</ShopHeading>
          </ShopHeader>
          <CatGrid>
            {PANDG_CATEGORIES.map((category, i) => (
              <CatTile
                key={category.key}
                to={getPandgSearchUrl(category)}
                onClick={scrollToTop}
              >
                {category.image ? (
                  <CatTileImg src={category.image} alt={category.label} />
                ) : (
                  <CatTileSwatch
                    {...PANDG_SWATCHES[i % PANDG_SWATCHES.length]}
                  />
                )}
                <CatTileBody>
                  <h4>{category.label}</h4>
                  <p>{category.description}</p>
                </CatTileBody>
              </CatTile>
            ))}
            <CatTileCta
              to={getPandgSearchUrl(PANDG_ALL_CATEGORY)}
              onClick={scrollToTop}
            >
              <CatTileCtaBody>
                <h4>View all products</h4>
                <p>Browse the complete P&amp;G PRO range</p>
              </CatTileCtaBody>
            </CatTileCta>
          </CatGrid>
        </Shop>
      </Wrap>

      <Wrap>
        <Trust>
          <TrustItem>
            <TrustNum>
              Next Day<TrustNumSmall>*</TrustNumSmall>
            </TrustNum>
            <TrustLabel>Delivery across the UK</TrustLabel>
          </TrustItem>
          <TrustItem>
            <TrustNum>1 Invoice</TrustNum>
            <TrustLabel>Combined with your food order</TrustLabel>
          </TrustItem>
          <TrustItem>
            <TrustNum>
              Mix More
              <br /> Save More
            </TrustNum>
            <TrustLabel>Any 10 products, unlock an instant discount</TrustLabel>
          </TrustItem>
          <TrustItem>
            <TrustNum>60+</TrustNum>
            <TrustLabel>P&G PRO Products to Shop</TrustLabel>
          </TrustItem>
        </Trust>
      </Wrap>

      <Wrap>
        <Testimonial>
          <TestimonialMark>&#10003;</TestimonialMark>
          <div>
            <TestimonialBody>
              JJ Foodservice is a UK-based, independent foodservice wholesaler
              and hygiene chemicals supplier, operating 14 branches nationwide.
              We're proud winners of the{' '}
              <strong>National Wholesaler of the Year</strong> 2026 at the
              Grocer Gold Awards, and{' '}
              <strong>Food & Drink Supplier of the Year</strong> at The Cateys
              Supplier Awards.
            </TestimonialBody>
            <TestimonialAttr>
              Trusted across the UK foodservice and hospitality sector
            </TestimonialAttr>
          </div>
        </Testimonial>
      </Wrap>

      <Wrap>
        <Faq>
          <FaqHeading>Common questions</FaqHeading>
          {PANDG_FAQ_ITEMS.map((item, i) => (
            <React.Fragment key={item.question}>
              <FaqItem onClick={() => toggleFaq(i)}>
                <FaqQuestion>{item.question}</FaqQuestion>
                <FaqIcon $open={openFaqIndex === i} />
              </FaqItem>
              <FaqAnswer $open={openFaqIndex === i}>
                <LinkifiedText text={item.answer} />
              </FaqAnswer>
            </React.Fragment>
          ))}
        </Faq>
        <SubText>{MESSAGES.DELIVERY_ASSIGNED_NOTICE}</SubText>
      </Wrap>

      <FooterCta>
        <div>
          <FooterCtaText>Ready to order?</FooterCtaText>
          <FooterCtaSub>
            The full P&amp;G PRO range, in stock for next-day delivery.
          </FooterCtaSub>
        </div>
        <PrimaryButton
          to={getPandgSearchUrl(PANDG_ALL_CATEGORY)}
          onClick={scrollToTop}
        >
          Shop the range
        </PrimaryButton>
      </FooterCta>
    </Page>
  );
};

export default PandGPage;
